import React, { ReactNode } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Emoji from 'common/emoji'
import { replaceNewLineEmojiAsElement } from 'common/variantsUtils'

import Cell from './Cell'

interface Column {
  title: string
  key: string
  dataIndex: string
}

interface Value {
  [key: string]: string | number
}

type Props = {
  results: {
    columns: Column[]
    values: Value[]
  } | null
  reports?: Record<string, number | string | null>
  showMachineOpensTicked?: boolean
}

const getEmptyCells = (count: number, key: string): ReactNode => {
  if (count <= 0) {
    return null
  }
  return Array.from(Array(count).keys()).map((index) => (
    <div key={`{${key}-${index}`} />
  ))
}

const WinnerVariantInfo: React.FC<Props> = ({
  results,
  reports,
  showMachineOpensTicked,
}) => {
  const openUplift = 'Open Uplift'
  const openUpliftMachine = 'Open Uplift Machine Open Excluded'
  const { showMachineOpens } = useFlags()
  const { performanceReport: shouldShowPerformanceReport } = useFlags()

  const hasReportsData =
    shouldShowPerformanceReport && reports && Object.keys(reports).length
  const hasResultsData = results

  if (!hasReportsData && !hasResultsData) {
    return null
  }

  const winnerVariant = results && results.values[0]['variant']
  const resultColumns =
    results?.columns.filter(({ key, dataIndex }) => {
      const value = results && results.values[0][dataIndex]
      return key !== 'variant' && value !== ''
    }) ?? []

  // if colCount or winnerColSpan changes, update the safelist in tailwind.config.js file
  const colCount = 7
  const resultsCellCount = resultColumns.length
  let reportsCellCount =
    shouldShowPerformanceReport && reports && Object.entries(reports).length

  if (reports && Object.keys(reports).includes(openUpliftMachine)) {
    reportsCellCount = reportsCellCount - 1
  }

  const totalCellCount = resultsCellCount + reportsCellCount

  const hasOneDataRow = totalCellCount <= colCount
  const hasReportsDataInWinnerRow = !hasOneDataRow && reportsCellCount <= 2
  const emptyReportsCellCount =
    hasOneDataRow || hasReportsDataInWinnerRow ? 0 : colCount - reportsCellCount
  const emptyResultsCellCount = hasOneDataRow
    ? colCount - totalCellCount
    : colCount - resultsCellCount

  const winnerColSpan = hasReportsDataInWinnerRow
    ? colCount - reportsCellCount
    : colCount

  // Temporary hide click uplift for in-body projects until the BE is fixed. HH-1774
  if (
    reports &&
    hasReportsData &&
    'In-Body Click Uplift' in reports &&
    'Click Uplift' in reports
  ) {
    delete reports['Click Uplift']
  }

  return (
    <div
      className={`grid grid-cols-${colCount} gap-6 px-6 pt-8 pb-6 
      border-b border-coolGray-200`}
      data-cy="winner-variant-info"
    >
      {winnerVariant && (
        <div className={`col-span-${winnerColSpan} flex items-center gap-6`}>
          <div className="w-15 h-15 rounded-full bg-emerald-200">
            <Emoji symbol="🏆" className="relative bottom-3 text-5xl" />
          </div>

          <div className="flex-grow text-base">
            <p className="text-orange-500 font-bold">
              And the winner at the end of the test was...
            </p>
            <p className="text-coolGray-800 font-medium">
              {replaceNewLineEmojiAsElement(winnerVariant as string)}
            </p>
          </div>
        </div>
      )}

      {shouldShowPerformanceReport &&
      reports &&
      Object.entries(reports).length ? (
        <>
          {Object.entries(reports)
            .filter((report) => {
              if (showMachineOpens) {
                if (
                  Object.entries(reports)
                    .flatMap((item) => item)
                    .includes(openUpliftMachine)
                ) {
                  if (
                    report[0] === openUpliftMachine &&
                    showMachineOpensTicked
                  ) {
                    return false
                  }
                  if (report[0] === openUplift && !showMachineOpensTicked) {
                    return false
                  }
                }
              }
              if (!showMachineOpens) {
                if (report[0] === openUpliftMachine) {
                  return false
                }
              }
              return true
            })
            .map(([key, value]) => {
              let formattedValue = value?.toLocaleString('en') || 'NA'
              const isUpliftValue = key.toLowerCase().includes('uplift')
              if (isUpliftValue && formattedValue !== 'NA') {
                formattedValue = `${formattedValue}%`
              }
              if (key === openUpliftMachine) {
                key = openUplift
              }
              return (
                <Cell
                  key={key}
                  title={key}
                  value={formattedValue}
                  valueClassName="text-gray-800"
                />
              )
            })}
          {getEmptyCells(emptyReportsCellCount, 'report')}
        </>
      ) : null}

      {resultColumns.map(({ key, title, dataIndex }) => {
        const value = results?.values[0][dataIndex]

        if (key === 'variant' || !value) {
          return undefined
        } else {
          return (
            <Cell
              key={key}
              title={title.replace('Final ', '')}
              value={value}
              valueClassName="text-orange-500"
            />
          )
        }
      })}
      {getEmptyCells(emptyResultsCellCount, 'result')}
    </div>
  )
}

export default WinnerVariantInfo
